import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import {catchError, tap, take} from 'rxjs/operators';
import { StoreSelectorsService } from '../store-selectors.service';
import { IUser } from '../../interfaces';

export interface RagQA {
    id: number;
    question: string;
    answer: string;
    category?: string;
    videoId?: string;
    createdDate?: Date;
    updatedDate?: Date;
    similarity?: number;
}

export interface RagResponse {
    success: boolean;
    data?: any;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class RagService {
    aiBaseUrl = "https://ai.signage.me/rag/";
    private authHeaders: HttpHeaders;

    constructor(
        private http: HttpClient,
        private storeSelector: StoreSelectorsService
    ) {
        // Initialize authentication headers from user credentials
        this.initializeAuthHeaders();
    }

    private initializeAuthHeaders(): void {
        this.storeSelector.getUser()
            .pipe(take(1))
            .subscribe((user: IUser) => {
                if (user && user.username && user.passwordNew) {
                    const credentials = btoa(`${user.username}:${user.passwordNew}`);
                    this.authHeaders = new HttpHeaders({
                        'Authorization': `Basic ${credentials}`,
                        'Content-Type': 'application/json'
                    });
                } else {
                    // Fallback if user credentials not available
                    console.warn('User credentials not available for RAG authentication');
                    this.authHeaders = new HttpHeaders({
                        'Content-Type': 'application/json'
                    });
                }
            });
    }

    // Refresh auth headers when user changes
    public refreshAuthHeaders(): void {
        this.initializeAuthHeaders();
    }

    getAllQA(): Observable<any> {
        const url = this.aiBaseUrl + 'all';
        return this.http.get(url, { headers: this.authHeaders });
    }

    addQA(qa: Partial<RagQA>): Observable<RagQA> {
        const url = this.aiBaseUrl + 'add';
        return this.http.post<RagQA>(url, {
            question: qa.question,
            answer: qa.answer,
            category: qa.category,
            videoId: qa.videoId
        }, { headers: this.authHeaders });
    }

    updateQA(qa: RagQA): Observable<RagQA> {
        const url = `${this.aiBaseUrl}${qa.id}`;
        return this.http.put<RagQA>(url, {
            id: qa.id,
            question: qa.question,
            answer: qa.answer,
            category: qa.category,
            videoId: qa.videoId
        }, { headers: this.authHeaders });
    }

    deleteQA(id: number): Observable<any> {
        const url = `${this.aiBaseUrl}${id}`;
        return this.http.delete(url, { headers: this.authHeaders });
    }

    searchSimilar(query: string, limit: number = 1, threshold: number = 0.7): Observable<RagQA[]> {
        const url = `${this.aiBaseUrl}search`;
        return this.http.post<RagQA[]>(url, {
            question: query,
            limit: limit,
            threshold: threshold
        }, { headers: this.authHeaders });
    }

    updateTutorials(tutorials: Array<{videoId: string, subject: string}>): Observable<any> {
        const url = `${this.aiBaseUrl}update-tutorials`;
        return this.http.post<any>(url, {
            tutorials: tutorials
        }, { headers: this.authHeaders });
    }
}
